import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Row, Col, Badge, Button, Spinner, Table, ListGroup } from "react-bootstrap";
import { FaArrowLeft, FaEdit, FaList, FaUsers, FaSitemap } from "react-icons/fa";
import { useGetChampById } from "../../hooks/CHAMPS/useGetChampById";
import { useGetChamps } from "../../hooks/CHAMPS/useGetChamps";
import EditChampModal from "../../components/Modals/Update/EditChamp";
import ManageFieldOptions from "../../components/Modals/Update/ManageFieldOptions";
import ManageFieldClients from "../../components/Modals/Update/ManageFieldClients";
import { useAuth } from "../../context/AuthContext";

const ChampDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetChampById(id);
  const { data: champsData } = useGetChamps();
  const { hasPermission } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showClientsModal, setShowClientsModal] = useState(false);

  const champ = data?.data;
  const allChamps = champsData?.data ?? [];

  const parentId = champ?.parentFieldId ?? null;
  const parentLabel = allChamps.find(c => c.id === parentId)?.label ?? null;

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <Spinner animation="border" role="status" style={{ color: "#f6c23e" }}>
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  if (isError || !champ) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger">
          Erreur lors du chargement des détails du champ
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">
          Détails du champ : {champ.label}
        </h1>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/administration?tab=champs')}>
            <FaArrowLeft className="me-2" />
            Retour
          </Button>
          {hasPermission('EDIT_CHAMP') && (
          <Button variant="warning" onClick={() => setShowEditModal(true)}>
            <FaEdit className="me-2" />
            Modifier
          </Button>
          )}
        </div>
      </div>

      <Row>
        <Col lg={6}>
          <Card className="shadow mb-4">
            <Card.Header className="py-3">
              <h6 className="m-0 font-weight-bold text-primary">
                Informations générales
              </h6>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}><strong>ID :</strong></Col>
                    <Col sm={8}>{champ.id}</Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}><strong>Label :</strong></Col>
                    <Col sm={8}>{champ.label}</Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}><strong>Nom de colonne :</strong></Col>
                    <Col sm={8}><code>{champ.column_name}</code></Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}><strong>Type de champ :</strong></Col>
                    <Col sm={8}>
                      {champ.field_type ? (
                        <Badge bg="info">{champ.field_type.label}</Badge>
                      ) : (
                        <span className="text-muted">Non défini</span>
                      )}
                    </Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}><strong>Statut d'application :</strong></Col>
                    <Col sm={8}>
                      {champ.field_apply_status ? (
                        <Badge bg="primary">{champ.field_apply_status}</Badge>
                      ) : (
                        <span className="text-muted">Non défini</span>
                      )}
                    </Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}>
                      <strong><FaSitemap className="me-1 text-warning" /> Champ parent :</strong>
                    </Col>
                    <Col sm={8}>
                      {parentId ? (
                        <Button
                          variant="link"
                          className="p-0 text-decoration-none"
                          onClick={() => navigate(`/administration-champs/${parentId}`)}
                        >
                          {parentLabel ?? `#${parentId}`}
                        </Button>
                      ) : (
                        <span className="text-muted">Aucun (champ racine)</span>
                      )}
                    </Col>
                  </Row>
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>

          <Card className="shadow mb-4">
            <Card.Header className="py-3">
              <h6 className="m-0 font-weight-bold text-primary">
                Configuration
              </h6>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}><strong>Position :</strong></Col>
                    <Col sm={8}>{champ.position}</Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}><strong>Largeur :</strong></Col>
                    <Col sm={8}>{champ.width}</Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}><strong>Règle de visibilité :</strong></Col>
                    <Col sm={8}>{champ.visibility_rule || <span className="text-muted">Aucune</span>}</Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}><strong>Valeur par défaut :</strong></Col>
                    <Col sm={8}>{champ.default_value || <span className="text-muted">Aucune</span>}</Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}><strong>Placeholder :</strong></Col>
                    <Col sm={8}>{champ.placeholder || <span className="text-muted">Aucun</span>}</Col>
                  </Row>
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="shadow mb-4">
            <Card.Header className="py-3">
              <h6 className="m-0 font-weight-bold text-primary">
                Paramètres
              </h6>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}><strong>Requis :</strong></Col>
                    <Col sm={8}>
                      <Badge bg={champ.is_required ? "success" : "secondary"}>
                        {champ.is_required ? "Oui" : "Non"}
                      </Badge>
                    </Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}><strong>Éditable :</strong></Col>
                    <Col sm={8}>
                      <Badge bg={champ.is_editable ? "success" : "secondary"}>
                        {champ.is_editable ? "Oui" : "Non"}
                      </Badge>
                    </Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}><strong>Actif :</strong></Col>
                    <Col sm={8}>
                      <Badge bg={champ.is_active ? "success" : "danger"}>
                        {champ.is_active ? "Actif" : "Inactif"}
                      </Badge>
                    </Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col sm={4}><strong>Tous les clients :</strong></Col>
                    <Col sm={8}>
                      <Badge bg={champ.all_clients ? "primary" : "secondary"}>
                        {champ.all_clients ? "Oui" : "Non"}
                      </Badge>
                    </Col>
                  </Row>
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>

          {champ.child_field && (
            <Card className="shadow mb-4">
              <Card.Header className="py-3">
                <h6 className="m-0 font-weight-bold text-primary">
                  Champ enfant
                </h6>
              </Card.Header>
              <Card.Body>
                <p><strong>Champ conditionnel :</strong> {champ.child_field.label}</p>
              </Card.Body>
            </Card>
          )}

          {champ.field_type?.name === 'Select' && champ.field_options && champ.field_options.length > 0 && (
            <Card className="shadow mb-4">
              <Card.Header className="py-3 d-flex justify-content-between align-items-center">
                <h6 className="m-0 font-weight-bold text-primary">
                  Options du champ ({champ.field_options.length})
                </h6>
                {hasPermission('EDIT_CHAMP') && (
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => setShowOptionsModal(true)}
                >
                  <FaList className="me-2" />
                  Gérer les options
                </Button>
                )}
              </Card.Header>
              <Card.Body>
                <Table striped hover size="sm">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Valeur</th>
                      <th>Label</th>
                    </tr>
                  </thead>
                  <tbody>
                    {champ.field_options.map((option) => (
                      <tr key={option.id}>
                        <td>{option.id}</td>
                        <td><code>{option.value}</code></td>
                        <td>{option.label}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {champ.field_type?.name === 'Select' && (!champ.field_options || champ.field_options.length === 0) && (
            <Card className="shadow mb-4">
              <Card.Header className="py-3 d-flex justify-content-between align-items-center">
                <h6 className="m-0 font-weight-bold text-primary">
                  Options du champ
                </h6>
                {hasPermission('EDIT_CHAMP') && (
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => setShowOptionsModal(true)}
                >
                  <FaList className="me-2" />
                  Gérer les options
                </Button>
                )}
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-0">Aucune option définie</p>
              </Card.Body>
            </Card>
          )}

          {champ.field_type?.name !== 'Select' && (
            <Card className="shadow mb-4 border-secondary">
              <Card.Header className="py-3 bg-secondary text-white">
                <h6 className="m-0 font-weight-bold">
                  <FaList className="me-2" />
                  Options du champ
                </h6>
              </Card.Header>
              <Card.Body>
                <p className="mb-0">
                  <Badge bg="secondary" className="me-2">Non applicable</Badge>
                  Les options ne sont disponibles que pour les champs de type "Select". Modifiez le type de champ pour définir des options.
                </p>
              </Card.Body>
            </Card>
          )}

          {!champ.all_clients && champ.clients && champ.clients.length > 0 && (
            <Card className="shadow mb-4">
              <Card.Header className="py-3 d-flex justify-content-between align-items-center">
                <h6 className="m-0 font-weight-bold text-primary">
                  Clients associés ({champ.clients.length})
                </h6>
                {hasPermission('EDIT_CHAMP') && (
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => setShowClientsModal(true)}
                >
                  <FaUsers className="me-2" />
                  Gérer les clients
                </Button>
                )}
              </Card.Header>
              <Card.Body>
                <ListGroup>
                  {champ.clients.map((client) => (
                    <ListGroup.Item key={client.id}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{client.name}</strong>
                          {client.address && (
                            <div className="text-muted small">{client.address}</div>
                          )}
                        </div>
                        <Badge bg={client.is_active ? "success" : "danger"}>
                          {client.is_active ? "Actif" : "Inactif"}
                        </Badge>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          )}

          {!champ.all_clients && (!champ.clients || champ.clients.length === 0) && (
            <Card className="shadow mb-4">
              <Card.Header className="py-3 d-flex justify-content-between align-items-center">
                <h6 className="m-0 font-weight-bold text-primary">
                  Clients associés
                </h6>
                
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => setShowClientsModal(true)}
                >
                  <FaUsers className="me-2" />
                  Gérer les clients
                </Button>
                
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-0">Aucun client associé</p>
              </Card.Body>
            </Card>
          )}

          {champ.all_clients && (
            <Card className="shadow mb-4 border-primary">
              <Card.Header className="py-3 bg-primary text-white">
                <h6 className="m-0 font-weight-bold">
                  <FaUsers className="me-2" />
                  Disponibilité
                </h6>
              </Card.Header>
              <Card.Body>
                <p className="mb-0">
                  <Badge bg="primary" className="me-2">Tous les clients</Badge>
                  Ce champ est disponible pour tous les clients. Décochez "Tous les clients" dans l'édition du champ pour gérer des clients spécifiques.
                </p>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      <EditChampModal
        show={showEditModal}
        setShow={setShowEditModal}
        champ={champ}
      />
      <ManageFieldOptions
        show={showOptionsModal}
        setShow={setShowOptionsModal}
        champ={champ}
      />
      <ManageFieldClients
        show={showClientsModal}
        setShow={setShowClientsModal}
        champ={champ}
      />
    </div>
  );
};

export default ChampDetail;
